import {
  EBillingStatus,
  EFastPaymentForCarrierStatus,
  EFreightForCarrierStatus,
  EFreightLoadingType,
  EFreightTransportGoodsType,
  EStopStatus,
  EStopType,
  EVehicleType,
} from '@okcargo-apps/shared-domain';
import {
  FastPaymentStatus,
  FreightBillingStatus,
  FreightLoadingType,
  FreightStatus,
  FreightStopStatus,
  FreightStopType,
  FreightTransportGoodsType,
  VehicleType,
} from '@okcargo/query-processor';

export function transformToEFreightForCarrierStatus(
  freightStatusResponse: FreightStatus,
  isAnyStopStartedOrFinished: boolean,
): EFreightForCarrierStatus {
  switch (freightStatusResponse) {
    case FreightStatus.PLANNING: // it should not be received if it is as a carrier, it should be received if it is as a shipper
    case FreightStatus.CREATED:
    case FreightStatus.ACCEPTED:
    case FreightStatus.PENDING:
    case FreightStatus.ASSIGNED:
      return isAnyStopStartedOrFinished ? EFreightForCarrierStatus.ON_ROUTE : EFreightForCarrierStatus.ASSIGNED;

    case FreightStatus.STARTED:
    case FreightStatus.ARRIVED:
    case FreightStatus.WORKING_START:
    case FreightStatus.WORKING_END:
    case FreightStatus.ON_ROUTE:
      return EFreightForCarrierStatus.ON_ROUTE;

    case FreightStatus.FINISHED:
    case FreightStatus.FINISHED_BY_CARRIER:
    case FreightStatus.FINISHED_BY_SHIPPER:
    case FreightStatus.FINISHED_WITH_ISSUE:
    case FreightStatus.FINISHED_WITH_ISSUE_SOLVED:
      return EFreightForCarrierStatus.FINISHED;

    case FreightStatus.CANCELLED:
    case FreightStatus.ABORTED: // but should not be received: in this state there would never have been an assignment to a carrier
    case FreightStatus.CANCELLED_BY_CARRIER:
    case FreightStatus.CANCELLED_BY_SHIPPER:
    case FreightStatus.OFFER_REJECTED:
    case FreightStatus.OFFER_REQUESTED:
    case FreightStatus.OFFER_SENT:
      return EFreightForCarrierStatus.CANCELLED;

    case FreightStatus.DRAFT:
    case FreightStatus.REJECTED:
    case FreightStatus.NO_SHOW_BY_CARRIER:
    case FreightStatus.NO_SHOW_BY_SHIPPER:
    case FreightStatus.DELETED:
      return EFreightForCarrierStatus.DELETED;
  }
}

export function mapFreightBillingStatusEnum(status: FreightBillingStatus): EBillingStatus {
  switch (status) {
    case FreightBillingStatus.NON_BILLABLE:
      return EBillingStatus.NON_BILLABLE;
    case FreightBillingStatus.READY_TO_INVOICE:
      return EBillingStatus.READY_TO_INVOICE;
    case FreightBillingStatus.INVOICED:
      return EBillingStatus.INVOICED;
    case FreightBillingStatus.OVERDUE:
      return EBillingStatus.OVERDUE;
    case FreightBillingStatus.PAID:
      return EBillingStatus.PAID;
  }
}

export function mapFastPaymentStatusEnum(status: FastPaymentStatus): EFastPaymentForCarrierStatus {
  switch (status) {
    case FastPaymentStatus.UNAVAILABLE:
    // TODO review. 2026-04-22 backend always return UNAVAILABLE
    // return EFastPaymentForCarrierStatus.UNAVAILABLE;
    case FastPaymentStatus.AVAILABLE:
      return EFastPaymentForCarrierStatus.AVAILABLE;
    case FastPaymentStatus.REQUESTED:
      return EFastPaymentForCarrierStatus.REQUESTED;
    case FastPaymentStatus.REJECTED:
      return EFastPaymentForCarrierStatus.REJECTED;
    case FastPaymentStatus.APPROVED:
      return EFastPaymentForCarrierStatus.APPROVED;
  }
}

export function mapStopTypeEnum(type: FreightStopType): EStopType {
  switch (type) {
    case FreightStopType.LOAD:
      return EStopType.LOAD;
    case FreightStopType.UNLOAD:
      return EStopType.UNLOAD;
    case FreightStopType.UNLOAD_LOAD:
      return EStopType.UNLOAD_LOAD;
    case FreightStopType.SWAP:
      return EStopType.UNLOAD_LOAD;
  }
}

export function mapStopStatusEnum(status: FreightStopStatus): EStopStatus {
  switch (status) {
    case FreightStopStatus.UNSTARTED:
      return EStopStatus.UNSTARTED;
    case FreightStopStatus.STARTED:
      return EStopStatus.STARTED;
    case FreightStopStatus.FINISHED:
      return EStopStatus.FINISHED;
  }
}

export function mapTransportGoodsTypeEnum(type: FreightTransportGoodsType): EFreightTransportGoodsType {
  switch (type) {
    case FreightTransportGoodsType.NORMAL:
      return EFreightTransportGoodsType.NORMAL;
    case FreightTransportGoodsType.ANIMAL:
      return EFreightTransportGoodsType.ANIMAL;
    case FreightTransportGoodsType.ADR:
      return EFreightTransportGoodsType.ADR;
    case FreightTransportGoodsType.WASTE:
      return EFreightTransportGoodsType.WASTE;
    case FreightTransportGoodsType.ADR_WASTE:
      return EFreightTransportGoodsType.ADR_WASTE;
  }
}

export function mapVehicleTypeEnum(type: VehicleType): EVehicleType {
  switch (type) {
    case VehicleType.TRACTOR:
      return EVehicleType.TRACTOR;
    case VehicleType.TRAILER:
      return EVehicleType.TRAILER;
    case VehicleType.BOX:
      return EVehicleType.BOX;
    case VehicleType.FRIGO:
      return EVehicleType.FRIGO;
    case VehicleType.TRUCK:
      return EVehicleType.TRUCK;
    case VehicleType.VAN:
      return EVehicleType.VAN;
    case VehicleType.MEGA:
      return EVehicleType.MEGA;
    case VehicleType.SMALL_TRAILER:
      return EVehicleType.SMALL_TRAILER;
    case VehicleType.MEGA_TRAILER:
      return EVehicleType.MEGA_TRAILER;
    case VehicleType.DUO_TRAILER:
      return EVehicleType.DUO_TRAILER;
    case VehicleType.GAS:
      return EVehicleType.GAS;
    case VehicleType.GAS_CARRIER:
      return EVehicleType.GAS_CARRIER;
    case VehicleType.BIODIESEL:
      return EVehicleType.BIODIESEL;
    case VehicleType.BIODIESEL_CARRIER:
      return EVehicleType.BIODIESEL_CARRIER;
    case VehicleType.TRAIN:
      return EVehicleType.TRAIN;
    case VehicleType.TRAIN_CARRIER:
      return EVehicleType.TRAIN_CARRIER;
    case VehicleType.EXCLUDED_CARRIER:
      return EVehicleType.EXCLUDED_CARRIER;
    case VehicleType.RIGID:
      return EVehicleType.RIGID;
    case VehicleType.CANVAS:
      return EVehicleType.CANVAS;
    case VehicleType.MOBILE_CANVAS:
      return EVehicleType.MOBILE_CANVAS;
    case VehicleType.RIGID_TRUCK:
      return EVehicleType.RIGID_TRUCK;
    case VehicleType.RIGID_CANVAS:
      return EVehicleType.RIGID_CANVAS;
    case VehicleType.REFRIGERATED:
      return EVehicleType.REFRIGERATED;
    case VehicleType.RIGID_REFRIGERATED:
      return EVehicleType.RIGID_REFRIGERATED;
    case VehicleType.BIREFRIGERATED:
      return EVehicleType.BIREFRIGERATED;
    case VehicleType.TRIREFRIGERATED:
      return EVehicleType.TRIREFRIGERATED;
    case VehicleType.BULK:
      return EVehicleType.BULK;
    case VehicleType.DUMP:
      return EVehicleType.DUMP;
    case VehicleType.TANKER:
      return EVehicleType.TANKER;
    case VehicleType.REFRIGERATED_TANKER:
      return EVehicleType.REFRIGERATED_TANKER;
    case VehicleType.CRANE:
      return EVehicleType.CRANE;
    case VehicleType.JUMBO_TRAILER:
      return EVehicleType.JUMBO_TRAILER;
    case VehicleType.OTHER:
      return EVehicleType.OTHER;
  }
}

export function mapLoadingTypeEnum(type: FreightLoadingType): EFreightLoadingType {
  switch (type) {
    case FreightLoadingType.REAR:
      return EFreightLoadingType.REAR;
    case FreightLoadingType.SIDE:
      return EFreightLoadingType.SIDE;
    case FreightLoadingType.TOP:
      return EFreightLoadingType.TOP;
  }
}
